/* NORDTAN — theme scripts (no dependencies) */
(() => {
  'use strict';

  const N = window.Nordtan || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  document.documentElement.classList.add('js');

  /* Money ---------------------------------------------------------------- */
  function formatMoney(cents, format = N.moneyFormat || '{{amount}} €') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2);
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2);
      }
    });
  }

  /* Header --------------------------------------------------------------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
  const mobileMenu = $('[data-mobile-menu]');
  if (mobileMenu) {
    mobileMenu.addEventListener('toggle', () => {
      if (mobileMenu.open && header) {
        const bottom = header.getBoundingClientRect().bottom;
        mobileMenu.style.setProperty('--menu-top', `${Math.max(bottom, 0)}px`);
      }
      document.body.classList.toggle('is-locked', mobileMenu.open);
      /* The header's blur effect would trap the fixed menu panel inside the header bar. */
      header?.classList.toggle('is-menu-open', mobileMenu.open);
    });
    $$('a', mobileMenu).forEach((a) => a.addEventListener('click', () => { mobileMenu.open = false; }));
  }

  /* Announcement rotator ------------------------------------------------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.index = 0;
      const interval = Number(this.dataset.interval) || 4000;
      this.timer = setInterval(() => this.next(), interval);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* Cart ----------------------------------------------------------------- */
  const SECTION_ID = 'cart-drawer';

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) {
          if (e.target.closest('a[data-cart-close]') && !e.target.closest('a').getAttribute('href').startsWith('#')) return;
          e.preventDefault();
          this.close();
        }
        const qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          const line = qtyBtn.closest('[data-line]');
          this.changeLine(Number(line.dataset.line), Number(qtyBtn.dataset.qtyChange));
        }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
    }
    open(trigger) {
      this.lastTrigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      requestAnimationFrame(() => $('.cart-drawer__panel', this)?.focus());
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      this.lastTrigger?.focus?.();
    }
    renderFromSections(sections) {
      const html = sections && sections[SECTION_ID];
      if (!html) return this.refresh();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('.cart-drawer__panel', doc);
      const panel = $('.cart-drawer__panel', this);
      if (fresh && panel) panel.innerHTML = fresh.innerHTML;
    }
    async refresh() {
      const res = await fetch(`${N.routes.root}?sections=${SECTION_ID}`);
      this.renderFromSections(await res.json());
    }
    async changeLine(line, quantity) {
      this.classList.add('is-loading');
      try {
        const res = await fetch(`${N.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [SECTION_ID], sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message);
        this.renderFromSections(data.sections);
        updateCartCount(data.item_count);
      } catch (err) {
        console.error(err);
        await this.refresh();
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);
  const drawer = () => $('cart-drawer');

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && N.cartType === 'drawer' && drawer()) {
      e.preventDefault();
      drawer().open(opener);
    }
  });

  async function addToCart(form, submitter) {
    const errorEl = $('[data-form-error]', form);
    const label = submitter && $('[data-add-label]', submitter);
    const original = label ? label.textContent : '';
    if (errorEl) errorEl.hidden = true;
    if (submitter) { submitter.disabled = true; submitter.setAttribute('aria-busy', 'true'); }
    if (label) label.textContent = N.strings.adding;
    try {
      const formId = form.getAttribute('id') || '';
      const sectionKey = formId.replace('ProductForm-', '');
      const info = sectionKey ? document.querySelector(`product-info[data-section="${sectionKey}"]`) : null;
      const items = info && info.bundleItems ? info.bundleItems() : null;
      let body;
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (items) {
        /* Pack with one colour/size per sleeve: one cart line per choice. */
        const payload = { items };
        if (N.cartType === 'drawer') { payload.sections = SECTION_ID; payload.sections_url = window.location.pathname; }
        body = JSON.stringify(payload);
        headers['Content-Type'] = 'application/json';
      } else {
        body = new FormData(form);
        if (N.cartType === 'drawer') {
          body.append('sections', SECTION_ID);
          body.append('sections_url', window.location.pathname);
        }
      }
      const res = await fetch(`${N.routes.cartAdd}.js`, { method: 'POST', headers, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || N.strings.error);
      if (N.cartType !== 'drawer' || !drawer()) { window.location.href = N.routes.cart; return; }
      drawer().renderFromSections(data.sections);
      const cart = await (await fetch(`${N.routes.cart}.js`)).json();
      updateCartCount(cart.item_count);
      drawer().open(submitter);
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message || N.strings.error; errorEl.hidden = false; }
    } finally {
      if (submitter) { submitter.removeAttribute('aria-busy'); submitter.disabled = submitter.dataset.soldOut === 'true'; }
      if (label) label.textContent = original;
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form.product-form');
    if (!form) return;
    e.preventDefault();
    addToCart(form, e.submitter || $('[data-add-button]', form));
  });

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = $('.qty__input', step.closest('.qty'));
    const next = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qtyStep));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Gallery -------------------------------------------------------------- */
  const handleize = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = $('[data-gallery-viewport]', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.dots = $$('.gallery__dot', this);
      this.activeGroup = this.dataset.activeGroup || '';
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.thumb)));
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.step(-1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.step(1));
      $('[data-gallery-3d]', this)?.addEventListener('click', (e) => this.show(e.currentTarget.dataset.gallery3d));
      this.initTilt();
      this.viewport.addEventListener('scroll', () => {
        if (!this.isMobile()) return;
        const i = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
        this.visibleDots().forEach((d, k) => d.classList.toggle('is-active', k === i));
      }, { passive: true });
    }
    isMobile() { return window.matchMedia('(max-width: 749px)').matches; }
    visibleSlides() { return this.slides.filter((s) => !s.hidden); }
    visibleDots() { return this.dots.filter((d) => !d.hidden); }
    initTilt() {
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!fine || calm) return;
      const max = 4;
      let frame;
      const target = () => $('.gallery__slide.is-active .gallery__img', this);
      this.viewport.addEventListener('pointermove', (e) => {
        const img = target();
        if (!img) return;
        const r = this.viewport.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          img.style.transform = `perspective(1100px) rotateY(${x * max}deg) rotateX(${-y * max}deg) scale(1.035)`;
          this.viewport.style.setProperty('--glare-x', `${(x + 0.5) * 100}%`);
          this.viewport.style.setProperty('--glare-y', `${(y + 0.5) * 100}%`);
        });
      });
      this.viewport.addEventListener('pointerleave', () => {
        cancelAnimationFrame(frame);
        $$('.gallery__img', this).forEach((img) => { img.style.transform = ''; });
      });
    }
    step(dir) {
      const list = this.visibleSlides();
      if (!list.length) return;
      const cur = Math.max(0, list.findIndex((s) => s.classList.contains('is-active')));
      const i = (cur + dir + list.length) % list.length;
      this.show(list[i].dataset.mediaId);
    }
    show(mediaId, instant) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide || slide.hidden) return false;
      this.slides.forEach((s) => s.classList.toggle('is-active', s === slide));
      this.classList.toggle('is-3d', !!slide.querySelector('[data-model]'));
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(mediaId)));
      const index = this.visibleSlides().indexOf(slide);
      this.visibleDots().forEach((d, k) => d.classList.toggle('is-active', k === index));
      if (this.isMobile()) this.viewport.scrollTo({ left: index * this.viewport.clientWidth, behavior: instant ? 'auto' : 'smooth' });
      return true;
    }
    /* Colour galleries: keep only the images of the chosen colour. */
    filterColor(value) {
      if (!this.hasAttribute('data-color-gallery')) return false;
      const grouped = this.slides.filter((s) => s.dataset.colorGroup);
      if (!grouped.length) return false;
      return this.showGroup(this.matchGroup(value) || grouped[0].dataset.colorGroup);
    }
    /* Group whose colour names match a variant value: exact name first, then partial (« Vert foncé » → Vert). */
    matchGroup(value) {
      const h = handleize(value);
      if (!h) return null;
      const groups = [];
      this.slides.forEach((s) => {
        if (s.dataset.colorGroup && !groups.some((g) => g.id === s.dataset.colorGroup)) {
          groups.push({ id: s.dataset.colorGroup, keys: (s.dataset.colors || '').split('|').filter(Boolean) });
        }
      });
      const exact = groups.find((g) => g.keys.includes(h));
      if (exact) return exact.id;
      const words = h.split('-');
      const partial = groups.find((g) => g.keys.some((k) => k.length > 2 && (words.includes(k) || h.includes(k) || k.includes(h))));
      return partial ? partial.id : null;
    }
    showGroup(group) {
      group = String(group);
      if (!this.slides.some((s) => s.dataset.colorGroup === group)) return false;
      if (group === this.activeGroup) return true;
      this.activeGroup = group;
      const toggle = (el) => { if (el.dataset.colorGroup) el.hidden = el.dataset.colorGroup !== group; };
      this.slides.forEach(toggle);
      this.dots.forEach(toggle);
      $$('.gallery__thumbs > li', this).forEach(toggle);
      const first = this.visibleSlides()[0];
      if (first) this.show(first.dataset.mediaId, true);
      const thumbs = $('.gallery__thumbs', this);
      if (thumbs) thumbs.scrollLeft = 0;
      return true;
    }
    firstThumbSrc() {
      const li = $$('.gallery__thumbs > li', this).find((el) => !el.hidden);
      return li ? $('img', li)?.currentSrc || $('img', li)?.src : '';
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* Variant picker ------------------------------------------------------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = $('[data-variants]', this);
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.section = this.closest('section') || document;
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) this.onChange();
        if (e.target.matches('[data-qty-break]')) this.updateBreaks();
        if (e.target.matches('[data-qty-input]')) this.updateBreaks();
        if (e.target.matches('[data-item-input]')) this.onItemChange(e.target);
      });
      this.addEventListener('input', (e) => { if (e.target.matches('[data-qty-input]')) this.updateBreaks(); });
      this.sortPacks();
      this.markAvailability();
      this.updatePackPrices();
      this.updateBreaks();
      requestAnimationFrame(() => this.syncColorGallery());
    }
    /* Packs: one colour and size per sleeve ------------------------------ */
    bundleCount() {
      const checked = $('[data-qty-break]:checked', this);
      return checked ? Number(checked.value) : 1;
    }
    bundleActive() {
      const panel = $('[data-bundle-items]', this);
      return !!panel && this.bundleCount() > 1;
    }
    rowValues(row) {
      const values = this.selectedOptions().slice();
      $$('[data-item-option]', row).forEach((el) => {
        const i = Number(el.dataset.itemOption);
        values[i] = el.tagName === 'SELECT' ? el.value : ($('input:checked', el)?.value ?? values[i]);
      });
      return values;
    }
    setRowValues(row, values) {
      $$('[data-item-option]', row).forEach((el) => {
        const v = values[Number(el.dataset.itemOption)];
        if (v == null) return;
        if (el.tagName === 'SELECT') el.value = v;
        else $$('input', el).forEach((input) => { input.checked = input.value === v; });
      });
    }
    syncBundleRows() {
      const main = this.selectedOptions();
      $$('[data-bundle-item]', this).forEach((row) => { if (!row.dataset.touched) this.setRowValues(row, main); });
      this.checkBundleRows();
    }
    onItemChange(input) {
      const row = input.closest('[data-bundle-item]');
      if (!row) return;
      row.dataset.touched = 'true';
      if (input.closest('[data-item-color]')) {
        $(`product-gallery[data-section="${this.dataset.section}"]`)?.filterColor?.(input.value);
      }
      this.checkBundleRows();
    }
    findVariant(values) {
      return this.variants.find((v) => v.options.every((o, i) => o === values[i]));
    }
    checkBundleRows() {
      if (!$('[data-bundle-items]', this)) return true;
      let ok = true;
      const n = this.bundleCount();
      $$('[data-bundle-item]', this).forEach((row) => {
        const k = Number(row.dataset.bundleItem);
        const v = this.findVariant(this.rowValues(row));
        const bad = k <= n && (!v || !v.available);
        const err = $('[data-item-error]', row);
        if (err) err.hidden = !bad;
        if (bad) ok = false;
      });
      if (this.bundleActive()) {
        const btn = $('[data-add-button]', this);
        if (btn && btn.dataset.soldOut !== 'true') btn.disabled = !ok;
      }
      return ok;
    }
    updateBundle() {
      const panel = $('[data-bundle-items]', this);
      if (!panel) return;
      const n = this.bundleCount();
      panel.hidden = n < 2;
      this.classList.toggle('is-bundle', n > 1);
      $$('[data-bundle-item]', panel).forEach((row) => { row.hidden = Number(row.dataset.bundleItem) > n; });
      if (n < 2) {
        const btn = $('[data-add-button]', this);
        if (btn) btn.disabled = btn.dataset.soldOut === 'true';
      }
      this.syncBundleRows();
    }
    /* Cart lines for the chosen pack, or null when the pack has no per-sleeve choice. */
    bundleItems() {
      if (!this.bundleActive()) return null;
      const qty = Math.max(1, Number($('[data-qty-input]', this)?.value) || 1);
      const n = this.bundleCount();
      const counts = new Map();
      for (const row of $$('[data-bundle-item]', this)) {
        const k = Number(row.dataset.bundleItem);
        if (k > n) continue;
        const v = this.findVariant(this.rowValues(row));
        if (!v || !v.available) {
          const label = $('.bundle-item__label', row)?.textContent || `#${k}`;
          throw new Error(`${label} : ${N.strings.unavailable}`);
        }
        counts.set(v.id, (counts.get(v.id) || 0) + qty);
      }
      return [...counts].map(([id, quantity]) => ({ id, quantity }));
    }
    currentVariant() {
      const id = Number($('[data-variant-id]', this)?.value);
      return this.variants.find((v) => v.id === id) || this.variants[0];
    }
    updateBreaks() {
      const v = this.currentVariant();
      if (!v) return;
      const fs = $('[data-qty-breaks]', this);
      const qtyInput = $('[data-qty-input]', this);
      const qty = Math.max(1, Math.min(99, parseInt(qtyInput?.value, 10) || 1));
      let n = 1;
      let packPrice = v.price;
      let base = v.price;
      if (fs) {
        base = Number(fs.dataset.p1) || v.price;
        const fixed = { 1: base, 2: Number(fs.dataset.p2), 3: Number(fs.dataset.p3) };
        const picker = fs.closest('.variant-picker');
        $$('[data-qty-break]', fs).forEach((input) => {
          const k = Number(input.value);
          const label = input.nextElementSibling;
          const price = fixed[k] || base * k;
          const compare = base * k;
          const priceEl = $('[data-break-price]', label);
          if (priceEl) priceEl.textContent = formatMoney(price);
          const cmp = $('[data-break-compare]', label);
          if (cmp) cmp.textContent = formatMoney(compare);
          const save = $('[data-break-save]', label);
          if (save) save.textContent = `-${Math.round((compare - price) * 100 / compare)}%`;
          const unit = $('[data-break-unit]', label);
          if (unit && k > 1) unit.textContent = (picker?.dataset.unitLabel || '[prix]').replace('[prix]', formatMoney(Math.floor(price / k)));
        });
        const checked = $('[data-qty-break]:checked', fs);
        n = checked ? Number(checked.value) : 1;
        packPrice = fixed[n] || base * n;
        const cur = $('[data-price-current]', this);
        if (cur) cur.textContent = formatMoney(packPrice);
        const cmpMain = $('[data-price-compare]', this);
        const badge = $('[data-price-badge]', this);
        const ref = base * n;
        if (cmpMain) { cmpMain.hidden = !(ref > packPrice); cmpMain.textContent = formatMoney(ref); }
        if (badge) { badge.hidden = !(ref > packPrice); badge.textContent = `-${Math.round((ref - packPrice) * 100 / ref)}%`; }
      }
      const total = packPrice * qty;
      const totalQty = $('[data-total-qty]', this);
      if (totalQty) totalQty.value = n * qty;
      const addPrice = $('[data-add-price]', this);
      if (addPrice) addPrice.textContent = `· ${formatMoney(total)}`;
      const sticky = $(`sticky-atc[data-section="${this.dataset.section}"] [data-sticky-price]`);
      if (sticky) sticky.textContent = `· ${formatMoney(total)}`;
      this.updateBundle();
    }
    packSize(value) {
      const m = String(value).match(/\d+/);
      return m ? Number(m[0]) : 1;
    }
    sortPacks() {
      $$('[data-pack-option] .variant-picker__values', this).forEach((wrap) => {
        const pairs = $$('input[data-option-input]', wrap).map((input) => [input, input.nextElementSibling]);
        pairs.sort((a, b) => this.packSize(a[0].value) - this.packSize(b[0].value));
        pairs.forEach(([input, label]) => { wrap.appendChild(input); wrap.appendChild(label); });
      });
    }
    updatePackPrices() {
      const fieldsets = $$('.variant-picker__option[data-option-index]', this);
      fieldsets.forEach((fs, idx) => {
        if (!fs.hasAttribute('data-pack-option')) return;
        const selected = this.selectedOptions();
        const priceFor = (value) => {
          const want = selected.slice(); want[idx] = value;
          return this.variants.find((v) => v.options.every((o, i) => o === want[i]));
        };
        const inputs = $$('input[data-option-input]', fs);
        const single = inputs.map((i) => ({ i, n: this.packSize(i.value), v: priceFor(i.value) })).sort((a, b) => a.n - b.n)[0];
        const unit = single && single.v ? single.v.price / single.n : null;
        const picker = fs.closest('.variant-picker');
        const imgs = (picker?.dataset.packImages || '').split('|');
        inputs.forEach((input) => {
          const label = input.nextElementSibling;
          const v = priceFor(input.value);
          const priceEl = $('[data-pack-price]', label);
          const saveEl = $('[data-pack-save]', label);
          const unitEl = $('[data-pack-unit]', label);
          const compareEl = $('[data-pack-compare]', label);
          const mediaEl = $('[data-pack-media]', label);
          if (priceEl) priceEl.textContent = v ? formatMoney(v.price) : '';
          const n = this.packSize(input.value);
          if (mediaEl && !mediaEl.firstChild) {
            const src = imgs[Math.min(n, 3) - 1];
            if (src) { const im = new Image(); im.src = src; im.alt = ''; im.loading = 'lazy'; mediaEl.appendChild(im); }
          }
          if (unitEl && v) {
            unitEl.textContent = n > 1
              ? (picker?.dataset.unitLabel || '[prix]').replace('[prix]', formatMoney(Math.floor(v.price / n)))
              : (picker?.dataset.singleLabel || '');
          }
          if (compareEl) {
            const ref = v && v.compare_at_price > v.price ? v.compare_at_price : (unit && n > 1 ? Math.round(unit * n) : 0);
            compareEl.hidden = !(v && ref > v.price);
            if (!compareEl.hidden) compareEl.textContent = formatMoney(ref);
          }
          if (saveEl && v && unit && n > 1) {
            const pct = Math.round((1 - v.price / (unit * n)) * 100);
            saveEl.hidden = pct < 1;
            saveEl.textContent = `-${pct}%`;
          } else if (saveEl) saveEl.hidden = true;
        });
      });
    }
    selectedOptions() {
      return $$('.variant-picker__option[data-option-index]', this).map((fs) => $('input:checked', fs)?.value);
    }
    onChange() {
      const selected = this.selectedOptions();
      $$('.variant-picker__option[data-option-index]', this).forEach((fs, i) => {
        const out = $('[data-selected-value]', fs);
        if (out) out.textContent = selected[i];
      });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.syncColorGallery(selected);
      this.markAvailability();
      this.updatePackPrices();
      this.update(variant);
    }
    syncColorGallery(selected) {
      const gallery = $(`product-gallery[data-section="${this.dataset.section}"]`);
      if (!gallery || !gallery.hasAttribute('data-color-gallery')) return;
      const fieldsets = $$('.variant-picker__option[data-option-index]', this);
      let ci = fieldsets.findIndex((fs) => fs.hasAttribute('data-color-option'));
      if (ci < 0) {
        /* Option not named « Couleur » : use the option whose values match the colour galleries. */
        ci = fieldsets.findIndex((fs) => $$('input[data-option-input]', fs).some((i) => gallery.matchGroup(i.value)));
      }
      if (ci < 0) return;
      gallery.filterColor((selected || this.selectedOptions())[ci]);
      const img = $(`sticky-atc[data-section="${this.dataset.section}"] [data-sticky-img]`);
      const src = gallery.firstThumbSrc && gallery.firstThumbSrc();
      if (img && src) img.src = src;
    }
    markAvailability() {
      const selected = this.selectedOptions();
      $$('.variant-picker__option[data-option-index]', this).forEach((fs, idx) => {
        $$('[data-option-input]', fs).forEach((input) => {
          const candidate = selected.slice();
          candidate[idx] = input.value;
          const ok = this.variants.some((v) => v.available && v.options.every((o, i) => o === candidate[i]));
          input.classList.toggle('is-unavailable', !ok);
        });
      });
    }
    update(variant) {
      const idInput = $('[data-variant-id]', this);
      const addBtn = $('[data-add-button]', this);
      const addLabel = $('[data-add-label]', this);
      const addPrice = $('[data-add-price]', this);
      const stock = $('[data-stock-note]', this);
      const sticky = $(`sticky-atc[data-section="${this.dataset.section}"]`);

      if (!variant) {
        if (addBtn) { addBtn.disabled = true; addBtn.dataset.soldOut = 'true'; }
        if (addLabel) addLabel.textContent = N.strings.unavailable;
        if (addPrice) addPrice.hidden = true;
        if (stock) stock.hidden = true;
        sticky?.update(null);
        return;
      }

      if (idInput) idInput.value = variant.id;
      const available = variant.available;
      if (addBtn) { addBtn.disabled = !available; addBtn.dataset.soldOut = String(!available); }
      if (addLabel) addLabel.textContent = available ? (addBtn?.dataset.labelAdd || N.strings.addToCart) : (addBtn?.dataset.labelSold || N.strings.soldOut);
      if (addPrice) { addPrice.hidden = false; addPrice.textContent = `· ${formatMoney(variant.price)}`; }
      if (stock) stock.hidden = !available;

      const priceEl = $('[data-price]', this);
      if (priceEl) {
        $('[data-price-current]', priceEl).textContent = formatMoney(variant.price);
        const compare = $('[data-price-compare]', priceEl);
        const badge = $('[data-price-badge]', priceEl);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compare.hidden = !onSale;
        priceEl.classList.toggle('price--sale', !!onSale);
        if (onSale) compare.textContent = formatMoney(variant.compare_at_price);
        if (badge) {
          badge.hidden = !onSale;
          if (onSale) badge.textContent = `-${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%`;
        }
      }

      if (variant.featured_media) {
        const gallery = $(`product-gallery[data-section="${this.dataset.section}"]`);
        if (gallery && !gallery.hasAttribute('data-color-gallery')) gallery.show(variant.featured_media.id);
      }

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      sticky?.update(variant);
      this.updateBreaks();
    }
  }
  customElements.define('product-info', ProductInfo);

  /* Sticky add to cart --------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(`ProductForm-${this.dataset.section}`);
      this.button = $('[data-sticky-add]', this);
      if (!this.form) return;
      const target = $('[data-add-button]', this.form) || this.form;
      const lbl = $('[data-sticky-label]', this);
      const ab = $('[data-add-button]', this.form);
      if (lbl && ab && ab.dataset.labelAdd) lbl.textContent = ab.disabled ? (ab.dataset.labelSold || lbl.textContent) : ab.dataset.labelAdd;
      const observer = new IntersectionObserver(([entry]) => {
        const passed = entry.boundingClientRect.top < 0;
        const show = !entry.isIntersecting && passed;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
      });
      observer.observe(target);
      this.button.addEventListener('click', () => {
        const submit = $('[data-add-button]', this.form);
        if (this.form.requestSubmit) this.form.requestSubmit(submit); else submit.click();
      });
    }
    update(variant) {
      const label = $('[data-sticky-label]', this);
      const price = $('[data-sticky-price]', this);
      const title = $('[data-sticky-variant]', this);
      if (!variant) {
        this.button.disabled = true;
        label.textContent = N.strings.unavailable;
        price.hidden = true;
        return;
      }
      this.button.disabled = !variant.available;
      const ab = this.form && $('[data-add-button]', this.form);
      label.textContent = variant.available ? (ab?.dataset.labelAdd || N.strings.addToCart) : (ab?.dataset.labelSold || N.strings.soldOut);
      price.hidden = false;
      price.textContent = `· ${formatMoney(variant.price)}`;
      if (title && variant.title !== 'Default Title') title.textContent = variant.title;
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* Delivery estimate ---------------------------------------------------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min) || 2;
      const max = Number(this.dataset.max) || 4;
      const cutoff = Number(this.dataset.cutoff) || 14;
      const lang = (document.documentElement.lang || 'fr').slice(0, 2);
      const now = new Date();
      const start = new Date(now);
      if (now.getHours() >= cutoff || [0, 6].includes(now.getDay())) start.setDate(start.getDate() + 1);
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (![0, 6].includes(d.getDay())) added += 1;
        }
        return d;
      };
      const fmt = new Intl.DateTimeFormat(lang === 'fr' ? 'fr-FR' : lang, { day: 'numeric', month: 'long' });
      const from = fmt.format(addBusinessDays(start, min));
      const to = fmt.format(addBusinessDays(start, max));
      const text = $('[data-delivery-text]', this);
      if (!text) return;
      const tpl = this.dataset.template;
      if (tpl) {
        const esc = (t) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        text.innerHTML = esc(tpl).replace('[debut]', `<strong>${from}</strong>`).replace('[fin]', `<strong>${to}</strong>`);
        return;
      }
      text.innerHTML = lang === 'fr'
        ? `Livraison estimée entre le <strong>${from}</strong> et le <strong>${to}</strong>`
        : `Estimated delivery between <strong>${from}</strong> and <strong>${to}</strong>`;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* Reveal on scroll ----------------------------------------------------- */
  const revealTargets = $$('.section-head, .steps__item, .iwt__media, .iwt__content, .compare__table, .colors__media, .review-card, .faq__list, .cta-banner__inner, .benefits__item');
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !window.Shopify?.designMode) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    revealTargets.forEach((el, i) => {
      el.classList.add('reveal');
      el.style.transitionDelay = `${(i % 3) * 80}ms`;
      io.observe(el);
    });
  }
})();
